import React, { useState, useContext, useEffect } from 'react';
import './index.scss';
import { TextField, InputAdornment } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { APIWebstoreContext } from '../../context/globalContext';
import { postAPICall } from '../../dataFetcher/axiosMethodCalls';
import { GROUP_PAYMENT_IDENTIFIER, ICP_IDENTIFIER } from '../../utils/constants';
import CustomTooltip from '../CustomTooltip/CustomTooltip';
import { Field } from '@sitecore-jss/sitecore-jss-react';
import DOMPurifyImport from 'dompurify';
import { getIdpToken } from '../../utils/authHelper';
import LoadingIcon from '../LoadingIcon/LoadingIcon';

export type GroupPaymentProps = {
  fields: {
    GroupPaymentTooltip: Field<string>;
    GroupPaymentConfirmation: Field<string>;
    IncorrectNumberMessage: Field<string>;
    ICPTotalText: Field<string>;
    RepeatedIncorrectNumberMessage: Field<string>;
  };
};

const DOMPurify = DOMPurifyImport as {
  sanitize: (dirty: string, config?: any) => string;
};

const GroupPayment = ({ fields }: GroupPaymentProps): React.JSX.Element => {
  const [groupPaymentChecked, setGroupPaymentChecked] = useState(false);
  const [groupNumber, setGroupNumber] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [incorrectAttemptCount, setIncorrectAttemptCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { state, dispatch } = useContext(APIWebstoreContext);

  const lobCounts = state?.cartCounts?.lobCounts ?? {};

  useEffect(() => {
    setGroupPaymentChecked(false);
    handleClear(isValid);
  }, [state?.toggleCalculateOrder]);

  const handleCheckboxChange = () => {
    setGroupPaymentChecked((prev) => {
      const newValue = !prev;
      if (!newValue) {
        handleClear(isValid);
      }
      return newValue;
    });
  };

  const handleValidateClick = async () => {
    if (groupNumber !== '') {
      setIsLoading(true);

      try {
        const idpToken = getIdpToken();

        const response = await postAPICall(
          `${process.env.REACT_APP_CART_URL}/checkout/groupnumber/${groupNumber}/validate`,
          {},
          {
            headers: {
              'B2C-Authorization': idpToken,
            },
          }
        );

        if (response && response.status === 204) {
          setIsValid(true);
          setErrorMessage('');
          setIncorrectAttemptCount(0);

          if (dispatch) {
            const updatedTotal =
              (state?.orderTotals?.totalAmount ?? 0) - (state?.orderTotals?.lobTotals?.ICP ?? 0);

            dispatch({
              type: 'CHECKOUT_UPDATE',
              payload: {
                orderDetails: {
                  ...state?.orderDetails,
                  ICP: {
                    paymentType: GROUP_PAYMENT_IDENTIFIER,
                    orderIds: state?.orderDetails?.ICP?.orderIds ?? [],
                  },
                },
                orderTotals: {
                  totalAmount: updatedTotal,
                  lobTotals: state?.orderTotals?.lobTotals ?? {},
                },
                isGroupPaymentSuccessful: true,
                paymentType:
                  !state?.paymentType || state?.paymentType === 'None'
                    ? GROUP_PAYMENT_IDENTIFIER
                    : state?.paymentType,
                groupNumber,
              },
            });
          }
        }
      } catch (error: any) {
        if (error.response?.status === 404) {
          const newCount = incorrectAttemptCount + 1;
          setIncorrectAttemptCount(newCount);

          setErrorMessage(
            newCount >= 3
              ? fields?.RepeatedIncorrectNumberMessage?.value
              : fields?.IncorrectNumberMessage?.value
          );
        } else if (error.response?.status === 401) {
          setErrorMessage('Unauthorized. Please log in again.');
        } else {
          setErrorMessage('Unable to validate Group Number');
          console.log(error);
        }
      } finally {
        setIsLoading(false);
      }
    } else {
      setErrorMessage('Group Number is Empty');
    }
  };

  const handleClear = (isValid: boolean) => {
    setGroupNumber('');
    setErrorMessage('');
    setIncorrectAttemptCount(0);

    if (dispatch && isValid) {
      setIsValid(false);
      const updatedTotal =
        (state?.orderTotals?.totalAmount ?? 0) + (state?.orderTotals?.lobTotals?.ICP ?? 0);

      const currentPaymentType =
        state?.paymentType === GROUP_PAYMENT_IDENTIFIER ? 'None' : state?.paymentType;

      dispatch({
        type: 'CHECKOUT_UPDATE',
        payload: {
          orderTotals: {
            totalAmount: updatedTotal,
            lobTotals: state?.orderTotals?.lobTotals ?? {},
          },
          isGroupPaymentSuccessful: false,
          groupNumber: '',
          paymentType: currentPaymentType,
          orderDetails: {
            ...state?.orderDetails,
            ICP: {
              paymentType: currentPaymentType ?? 'None',
              orderIds: state?.orderDetails?.ICP?.orderIds ?? [],
            },
          },
        },
      });
    }
  };

  function getGroupPaymentConfirmationText(GroupPaymentConfirmation: Field<string>) {
    const rawHtml = GroupPaymentConfirmation?.value ?? '';

    const updatedHtml = rawHtml.replace(
      '##REMAINING_BALANCE##',
      `$${state?.orderTotals?.totalAmount.toFixed(2) ?? '0.00'}`
    );

    const sanitizedHtml = DOMPurify.sanitize(updatedHtml);

    return <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
  }

  return (
    <div className="group-payment">
      <div className="radiodiv">
        <label htmlFor="GroupPayment" className="radio-label">
          <input
            type="checkbox"
            id="GroupPayment"
            name="GroupPayment"
            className="radiobutton"
            checked={groupPaymentChecked}
            onChange={handleCheckboxChange}
          />
          <div className="paymenttype">Group Payment</div>
          <CustomTooltip fields={{ 'Tooltip Text': fields?.GroupPaymentTooltip }} />
        </label>
      </div>

      {groupPaymentChecked && (
        <div className="group-payment-inline">
          <h3>
            {fields?.ICPTotalText?.value}: $
            {state?.orderTotals?.lobTotals?.ICP?.toFixed(2) ?? '0.00'}
          </h3>
          <h3>Group Number:</h3>

          <div className="input-button-row">
            <TextField
              id="outlined-basic"
              variant="outlined"
              size="small"
              value={groupNumber}
              onChange={(e) => setGroupNumber(e.target.value)}
              error={!!errorMessage}
              autoComplete="off"
              disabled={isValid}
              InputProps={{
                endAdornment: isValid ? (
                  <InputAdornment position="end">
                    <CheckCircleOutlineIcon />
                  </InputAdornment>
                ) : null,
              }}
            />
            {!isValid ? (
              <button className="validate-btn" onClick={handleValidateClick} disabled={isLoading}>
                {isLoading ? <LoadingIcon /> : 'Validate Group Number'}
              </button>
            ) : (
              <button className="clear-btn" onClick={() => handleClear(true)}>
                Clear
              </button>
            )}
          </div>
          {errorMessage && <div className="error-message">{errorMessage}</div>}

          <h6 className="order-confirmation">
            {isValid ? getGroupPaymentConfirmationText(fields?.GroupPaymentConfirmation) : ''}
          </h6>
        </div>
      )}
    </div>
  );
};

export default GroupPayment;
